import { FormFieldWithIcon, NotificationHub, FontAwesomeIcon, RichTable } from "@nef/core"
import { useCallback, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Loader } from "@nef/icons"

import { AppDispatch } from "../../app/store"
import { searchInstrumentsByToken } from "../../redux/instrument/actions"
import {debounce} from "../../utils/common"
import { instrumentSearchColumns } from "../../constants/table/expenseManagerColumns"
import { TABLE_EMPTY_API_DATA, TABLE_EMPTY_STATE_MESSAGE } from "../../constants/common"
import { IInstrumentSearch } from "../../types/InstrumentExpense"

const InstrumentSearch = ({ onInstrumentSelect, showBackdrop = false, bodyHeight = 200, params }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useDispatch<AppDispatch>()
  const [searchResults, setSearchResults] = useState<IInstrumentSearch[]>([])
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string>("")
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const searchRef = useRef<HTMLInputElement>(null)

  // handler for the instrument search
  const handleInstrumentSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value)
    setSearchResults([])
    if (!e.target.value || e.target.value.length <= 3) return
    setHasMore(true)
    debouncedSearch(e.target.value)
  }

  // api to get the search results
  const getSearchData = async (token: string, page = 1) => {
    setPageNumber(page)
    try {
      setIsLoading(true)
      // make API call here using token
      const res = await dispatch(searchInstrumentsByToken({ token, pageNumber: page, params })).unwrap()
      if (res?.length && searchRef?.current?.value === token) setSearchResults((prev: any) => [...prev, ...res])
      else setHasMore(false)
      setIsLoading(false)
    } catch (error: any) {
      NotificationHub.send("danger", `${error.status} ${error.message}`)
      setIsLoading(false)
    }
  }

  const debouncedSearch = useCallback(debounce(getSearchData), [])

  const handleInfiniteScroll = useCallback(
    (e: any) => {
      if (e.scrollDirection === "forward" && e.isNearEnd && e.scrollTop > 0 && hasMore) debouncedSearch(searchString, pageNumber + 1)
    },
    [pageNumber, searchString, hasMore]
  )

  // prevents scrolling when backdrop is active
  const showDropDown = () => {
    setShowSearchDropdown(true)
    if (!showBackdrop) return
    const el: HTMLElement | null = document.querySelector(".sidebar-content")
    el?.classList?.add("overflow-y-hidden")
  }

  // enables scrolling when backdrop is active
  const hideDropDown = () => {
    setShowSearchDropdown(false)
    if (!showBackdrop) return
    const el: HTMLElement | null = document.querySelector(".sidebar-content")
    el?.classList?.remove("overflow-y-hidden")
  }

  return (
    <>
      <div className="instrument__container">
        <div className="instrument-search__search-wrapper">
          <FormFieldWithIcon
            className="instrument-search__search"
            label=""
            placeholder="Search with NSM, Symbol, CUSIP, ISIN"
            name="instrumentSearch"
            onChange={handleInstrumentSearch}
            iconClassName="fa-search"
            addonType="append"
            optional
            onFocus={showDropDown}
            autoComplete="off"
            ref={searchRef}
            data-testid="instrumentSearch"
          />
          {isLoading && <Loader color="primary" intensity={600} size="md" className="instrument-search__spinner" />}

          {showSearchDropdown && (
            <>
              {showBackdrop && (
                // this is just a backdrop element to get bubbling events so the dropdown can be closed on outside click
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
                <div className="instrument-search__backdrop" aria-hidden onClick={hideDropDown} />
              )}
              <div className="instrument-search__dropdown">
                <div className="instrument-search__close">
                  <FontAwesomeIcon className="instrument-search__close-icon" iconClassName="fa-times" onClick={hideDropDown} />
                </div>
                <RichTable
                  className="rich-table"
                  isHoverOnRowEnabled
                  bodyHeight={bodyHeight}
                  options={{
                    columns: instrumentSearchColumns(),
                    data: searchResults
                  }}
                  isPaginationEnabled={false}
                  rowBgPattern="border"
                  emptyStateMessage={searchResults.length ? TABLE_EMPTY_STATE_MESSAGE : TABLE_EMPTY_API_DATA}
                  loading={isLoading && pageNumber === 1}
                  onRowClick={(e: any) => {
                    onInstrumentSelect(e)
                    hideDropDown()
                  }}
                  onInfiniteScrollChange={handleInfiniteScroll}
                />
              </div>
            </>
          )}
        </div>
        <br />
      </div>
    </>
  )
}

export default InstrumentSearch
